/**
 * Utilidades para manejo de productos con configuración de gustos
 */

/**
 * Verifica si un producto requiere selección de gustos
 * @param {Object} product - El producto a verificar
 * @returns {boolean} true si requiere selección de gustos
 */
export function requiresFlavorSelection(product) {
    if (!product) {
        return false;
    }
    
    return product?.hasFlavorConfiguration && 
           product?.productConfiguration !== null &&
           product?.productConfiguration !== undefined &&
           product?.productConfiguration?.allowFlavorSelection;
}

/**
 * Obtiene el máximo número de gustos permitidos para un producto
 * @param {Object} product - El producto
 * @returns {number} Número máximo de gustos (0 si no tiene configuración)
 */
export function getMaxFlavors(product) {
    return product.productConfiguration?.maxFlavors || 0;
}

/**
 * Verifica si un producto tiene configuración de gustos (sin importar si está activa)
 * @param {Object} product - El producto a verificar
 * @returns {boolean} true si tiene configuración
 */
export function hasFlavorConfiguration(product) {
    return product?.hasFlavorConfiguration && 
           product?.productConfiguration !== null && 
           product?.productConfiguration !== undefined;
}

/**
 * Genera el texto descriptivo para mostrar la configuración de gustos
 * @param {Object} product - El producto
 * @returns {string} Texto descriptivo (ej: "Hasta 2 gustos")
 */
export function getFlavorConfigText(product) {
    if (!hasFlavorConfiguration(product)) {
        return '';
    }
    
    const maxFlavors = getMaxFlavors(product);
    if (maxFlavors === 1) {
        return '1 gusto';
    } else if (maxFlavors > 1) {
        return `Hasta ${maxFlavors} gustos`;
    }
    
    return '';
}

/**
 * Verifica si un producto con gustos está listo para agregar al carrito
 * @param {Object} product - El producto (puede incluir selectedFlavors)
 * @returns {boolean} true si está listo para agregar
 */
export function isProductReadyForCart(product) {
    if (!requiresFlavorSelection(product)) {
        return true; // Productos sin configuración siempre están listos
    }
    
    // Para productos con configuración, debe tener gustos seleccionados
    return product.selectedFlavors && product.selectedFlavors.length > 0;
}

/**
 * Genera un ID único para productos con gustos seleccionados
 * @param {Object} product - El producto con gustos
 * @returns {string} ID único para el carrito
 */
export function generateCartProductId(product) {
    if (!product.selectedFlavors || product.selectedFlavors.length === 0) {
        return product.id.toString();
    }
    
    // Crear ID único basado en producto + gustos seleccionados
    const flavorIds = product.selectedFlavors.map(f => f.id).sort().join('-');
    return `${product.id}_flavors_${flavorIds}`;
}

/**
 * Formatea el nombre del producto para mostrar en el carrito incluyendo gustos
 * @param {Object} product - El producto con gustos
 * @returns {string} Nombre formateado
 */
export function formatProductNameWithFlavors(product) {
    if (!product.selectedFlavors || product.selectedFlavors.length === 0) {
        return product.name;
    }
    
    const flavorNames = product.selectedFlavors.map(f => f.name).join(', ');
    return `${product.name} (${flavorNames})`;
}

/**
 * Prepara un producto para agregar al carrito, incluyendo información de gustos
 * @param {Object} product - El producto original
 * @returns {Object} Producto preparado para el carrito
 */
export function prepareProductForCart(product) {
    const cartProduct = {
        id: generateCartProductId(product),
        originalId: product.id, // Mantener ID original para referencia
        name: formatProductNameWithFlavors(product),
        brandName: product.brandName,
        description: product.description,
        price: product.price,
        cant: 1
    };
    
    // Agregar información de gustos si aplica
    if (product.selectedFlavors && product.selectedFlavors.length > 0) {
        cartProduct.selectedFlavors = product.selectedFlavors;
        cartProduct.flavorInfo = product.flavorInfo || product.selectedFlavors.map(f => f.name).join(', ');
        cartProduct.hasFlavorConfiguration = true;
    }
    
    return cartProduct;
}